import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { progressAPI, courseAPI } from '../api';
import { FiBook, FiAward, FiTrendingUp, FiPlay, FiClock, FiUsers } from 'react-icons/fi';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const [progress, setProgress] = useState([]);
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    totalLessons: 0,
    completedLessons: 0
  });
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progressRes, statsRes, coursesRes] = await Promise.all([
        progressAPI.getProgress(),
        progressAPI.getStats(),
        courseAPI.getFeatured()
      ]);
      
      setProgress(progressRes.data.progress || []);
      setStats(statsRes.data.stats || {
        totalCourses: 0,
        completedCourses: 0,
        totalLessons: 0,
        completedLessons: 0
      });
      setFeaturedCourses(coursesRes.data.courses || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      daily: '🗣️',
      business: '💼',
      travel: '✈️',
      grammar: '📝',
      pronunciation: '🔊',
      ielts: '📚',
      toefl: '🎓'
    };
    return icons[category] || '📖';
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '60vh' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="container">
          <h1 className="page-header-title">Welcome back, {user?.username}!</h1>
          <p className="page-header-text">Continue your English learning journey</p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-icon primary">
                <FiBook />
              </div>
              <div className="stat-value">{stats.totalCourses}</div>
              <div className="stat-label">Enrolled Courses</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon secondary">
                <FiAward />
              </div>
              <div className="stat-value">{stats.completedCourses}</div>
              <div className="stat-label">Completed Courses</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon warning">
                <FiTrendingUp />
              </div>
              <div className="stat-value">{stats.completedLessons}</div>
              <div className="stat-label">Lessons Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon danger">
                <FiClock />
              </div>
              <div className="stat-value">{user?.totalPoints || 0}</div>
              <div className="stat-label">Total Points</div>
            </div>
          </div>

          {progress.length > 0 ? (
            <div style={{ marginBottom: '40px' }}>
              <h2 className="section-title">Continue Learning</h2>
              <div className="courses-grid">
                {progress.slice(0, 3).map((p) => (
                  <Link 
                    to={`/courses/${p.course?._id}`} 
                    key={p._id} 
                    className="card course-card"
                  >
                    <div className="course-card-image">
                      {getCategoryIcon(p.course?.category)}
                    </div>
                    <div className="course-card-body">
                      <h3 className="course-card-title">{p.course?.title}</h3>
                      <div className="course-card-meta">
                        <span>{p.course?.level}</span>
                      </div>
                      <div className="course-card-footer">
                        <div className="course-card-progress">
                          <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${p.overallProgress}%` }}></div>
                          </div>
                          <span className="progress-text">{p.overallProgress}% complete</span>
                        </div>
                        <button className="btn btn-primary" style={{ padding: '8px 16px' }}>
                          <FiPlay /> Continue
                        </button>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: '40px', marginBottom: '40px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
              <h3 style={{ marginBottom: '10px' }}>No courses yet</h3>
              <p style={{ color: 'var(--gray)', marginBottom: '20px' }}>
                Start your learning journey by enrolling in a course
              </p>
              <Link to="/courses" className="btn btn-primary">
                Browse Courses
              </Link>
            </div>
          )}

          <div>
            <h2 className="section-title">Recommended Courses</h2>
            <div className="courses-grid">
              {featuredCourses.slice(0, 3).map((course) => (
                <Link 
                  to={`/courses/${course._id}`} 
                  key={course._id} 
                  className="card course-card"
                >
                  <div className="course-card-image">
                    {getCategoryIcon(course.category)}
                  </div>
                  <div className="course-card-body">
                    <h3 className="course-card-title">{course.title}</h3>
                    <div className="course-card-meta">
                      <span className="course-card-meta-item">
                        <FiBook /> {course.lessons?.length || 0} Lessons
                      </span>
                      <span className="course-card-meta-item">
                        <FiUsers /> {course.enrolledCount || 0}
                      </span>
                    </div>
                    <div className="course-card-footer">
                      <span className={`badge badge-${course.level === 'beginner' ? 'success' : course.level === 'intermediate' ? 'warning' : 'primary'}`}>
                        {course.level}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
